// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// The coordinate math behind the visual e-signature editor. Isolated + pure so the conversion that
// determines WHERE a signature lands on the signed document is unit-tested, not eyeballed.
//
// Three coordinate frames are in play:
//   1. Screen pixels — where the admin drops a field on the rendered PDF page image, at some zoom.
//   2. PDF points (72/inch) — the document's own unit; DocuSign tabs use points.
//   3. Origin differs: a rendered image is top-left origin (y grows downward); DocuSign tabs are
//      ALSO top-left origin per page — so once we're in points, no Y-flip is needed for DocuSign.
//      (PDF's native content stream is bottom-left, but we never place into the content stream; we
//      hand DocuSign top-left point coordinates, which is what its API expects.)
//
// The editor renders each page at scale = renderedPixelWidth / pagePointWidth. To store a drop:
//   pointX = pixelX / scale ;  pointY = pixelY / scale
// Round-tripping is exact up to rounding, and is INDEPENDENT of the zoom the admin used.

export interface PagePoints { page: number; w: number; h: number }   // PDF point dimensions
export interface PixelDrop { page: number; pixelX: number; pixelY: number; renderedWidthPx: number }
export interface PointPosition { page: number; x: number; y: number }

/** Scale factor for a page rendered at renderedWidthPx given its PDF point width. */
export function renderScale(pagePointWidth: number, renderedWidthPx: number): number {
  if (pagePointWidth <= 0) throw new Error('page point width must be positive')
  return renderedWidthPx / pagePointWidth
}

/** Convert a pixel drop on the rendered page into PDF-point, top-left-origin coordinates. */
export function pixelToPoint(drop: PixelDrop, page: PagePoints): PointPosition {
  if (drop.page !== page.page) throw new Error('drop/page mismatch')
  const scale = renderScale(page.w, drop.renderedWidthPx)
  return {
    page: page.page,
    x: round3(drop.pixelX / scale),
    y: round3(drop.pixelY / scale),
  }
}

/** Inverse: place a stored point position back onto a page rendered at a (possibly different) width. */
export function pointToPixel(pos: PointPosition, page: PagePoints, renderedWidthPx: number): { left: number; top: number } {
  const scale = renderScale(page.w, renderedWidthPx)
  return { left: pos.x * scale, top: pos.y * scale }
}

/**
 * Validate a placement sits inside the page and leaves room for the field box. Returns an error
 * string or null. Prevents the silent failure where a field is placed off-page and DocuSign either
 * rejects the envelope or clips the tab.
 */
export function validatePlacement(
  pos: PointPosition,
  page: PagePoints,
  fieldWidth = 0,
  fieldHeight = 0,
): string | null {
  if (pos.page < 1) return 'page number must be >= 1'
  if (pos.x < 0 || pos.y < 0) return 'position cannot be negative'
  if (pos.x + fieldWidth > page.w) return `field extends past right edge (x ${pos.x} + w ${fieldWidth} > ${page.w})`
  if (pos.y + fieldHeight > page.h) return `field extends past bottom edge (y ${pos.y} + h ${fieldHeight} > ${page.h})`
  return null
}

/** Default field box sizes in points, used when the editor doesn't specify width/height. */
export const DEFAULT_FIELD_SIZE: Record<string, { w: number; h: number }> = {
  signature: { w: 144, h: 36 },
  date_signed: { w: 96, h: 24 },
  initials: { w: 48, h: 36 },
  text: { w: 144, h: 24 },
  checkbox: { w: 16, h: 16 },
  name: { w: 144, h: 24 },
  title: { w: 144, h: 24 },
}

export const FIELD_TYPES = [
  'signature', 'date_signed', 'initials', 'text', 'checkbox', 'name', 'title',
] as const
export type FieldType = typeof FIELD_TYPES[number]

/** Human labels for the palette. */
export const FIELD_LABEL: Record<FieldType, string> = {
  signature: 'Signature',
  date_signed: 'Date Signed',
  initials: 'Initials',
  text: 'Text',
  checkbox: 'Checkbox',
  name: 'Name',
  title: 'Title',
}

function round3(n: number): number { return Math.round(n * 1000) / 1000 }
